// People (MUI, /people). Presentation port of PeoplePage — same data/mutations
// (listAdminProfiles, updateAdminProfile), rebuilt with mui/* primitives + brainTokens.
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import type { AdminProfile } from "../../lib/brain-api";
import { listAdminProfiles, updateAdminProfile } from "../../lib/brain-api";
import { useProfile } from "../../lib/profile";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, SectionCard, StatusPill, Icon } from "../../mui";

const c = brainTokens.color;
const TEAMS = ["devops", "dev", "product", "support", "sales", "finance"] as const;

function EditDialog({
  profile,
  onClose,
  onSave,
}: {
  profile: AdminProfile;
  onClose: () => void;
  onSave: (updated: AdminProfile) => void;
}) {
  const [departments, setDepartments] = useState<string[]>(profile.departments ?? []);
  const [role, setRole] = useState(profile.role === "admin" ? "admin" : "member");
  const [orgMember, setOrgMember] = useState(Boolean(profile.orgMember));
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateAdminProfile(profile.userId, { departments, role, orgMember });
      enqueueSnackbar("Profile updated", { variant: "success" });
      onSave(updated);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Update failed", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const toggleDept = (team: string) => {
    setDepartments((prev) => (prev.includes(team) ? prev.filter((d) => d !== team) : [...prev, team]));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit profile — {profile.displayName ?? profile.email ?? profile.userId}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
        <Box>
          <Box sx={{ fontSize: 12, color: c.muted2, mb: 1 }}>Departments</Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {TEAMS.map((team) => {
              const selected = departments.includes(team);
              return (
                <Button
                  key={team}
                  size="small"
                  variant={selected ? "contained" : "outlined"}
                  onClick={() => toggleDept(team)}
                  startIcon={selected ? <Icon name="check" size={13} /> : undefined}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    ...(selected
                      ? { bgcolor: c.tealTint, color: c.tealInk2, borderColor: c.teal, "&:hover": { bgcolor: c.tealTint } }
                      : { color: c.muted2, borderColor: c.border }),
                  }}
                >
                  {team}
                </Button>
              );
            })}
          </Box>
        </Box>
        <Box>
          <Box sx={{ fontSize: 12, color: c.muted2, mb: 1 }}>Role</Box>
          <Select size="small" value={role} onChange={(e) => setRole(e.target.value)} sx={{ minWidth: 160, fontSize: 13 }}>
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        </Box>
        <FormControlLabel
          sx={{ m: 0, alignItems: "flex-start", "& .MuiFormControlLabel-label": { fontSize: 13, color: c.ink3 } }}
          control={<Checkbox checked={orgMember} onChange={(e) => setOrgMember(e.target.checked)} />}
          label="Org member (grants company/dept access + quota bypass)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ color: c.ink3 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PeoplePageMui() {
  const { profile: myProfile } = useProfile();
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminProfile | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAdminProfiles();
      setProfiles(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = (updated: AdminProfile) => {
    setProfiles((prev) => prev.map((p) => (p.userId === updated.userId ? updated : p)));
    setEditing(null);
  };

  return (
    <Box>
      <PageHeader title="People" eyebrow="Access governance" />

      {error && (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            fontSize: 13,
            color: c.dangerText,
            bgcolor: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: `${brainTokens.radius.panel}px`,
            px: 2,
            py: 1.25,
          }}
        >
          {error}
        </Box>
      )}

      <SectionCard padding={0} sx={{ mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell sx={{ width: 100 }}>Role</TableCell>
                <TableCell sx={{ width: 80 }}>Org</TableCell>
                <TableCell>Departments</TableCell>
                <TableCell sx={{ width: 90 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: c.muted2, py: 4, textAlign: "center" }}>
                    Loading…
                  </TableCell>
                </TableRow>
              ) : profiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: c.muted2, py: 4, textAlign: "center" }}>
                    No rows.
                  </TableCell>
                </TableRow>
              ) : (
                profiles.map((row) => (
                  <TableRow key={row.userId} hover>
                    <TableCell sx={{ fontWeight: 500, color: c.ink }}>
                      {row.displayName ?? row.email ?? row.userId}
                    </TableCell>
                    <TableCell sx={{ color: c.muted2 }}>{row.email}</TableCell>
                    <TableCell>
                      <StatusPill tone={row.role === "admin" ? "teal" : "neutral"} label={row.role === "admin" ? "Admin" : "Member"} />
                    </TableCell>
                    <TableCell>
                      {row.orgMember ? <Icon name="check" size={15} style={{ color: c.tealInk2 }} /> : null}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {(row.departments ?? []).map((d) => (
                          <StatusPill key={d} tone="neutral" label={d} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.userId !== myProfile?.userId ? (
                        <Button size="small" variant="outlined" startIcon={<Icon name="pencil" size={14} />} onClick={() => setEditing(row)}>
                          Edit
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {editing && <EditDialog profile={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
    </Box>
  );
}
